import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { format, isBefore, isSameDay, isValid } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, CollegeInfo, CollegeTermInfo, TestThresholdInfo } from '../../services/application/application.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';


@Component({
    selector: 'app-manage-scores',
    styleUrls: ['managescores.component.scss'],
    templateUrl: 'managescores.component.html',
})
export class ManageScoresComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;
    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['TestDescription', 'Description', 'ScoreType', 'Score', 'Active', 'Actions'];
    FilterText = '';
    FilteredTests: TestThresholdInfo[] = [];
    NoData: boolean;
    OnlyActive = true;
    PartialViewReady: boolean;
    SmallDisplay: boolean;
    TestsData: DataTableSource<TestThresholdInfo>;
    TestsList: TestThresholdInfo[];
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.NoData = false;
        self.PartialViewReady = false;
        self.TestsList = null;
        self.TestsData = new DataTableSource<TestThresholdInfo>([], self.paginator, self.sorter, self.SortTests.bind(self));
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe((media) => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first((user) => user && !!user.AuthToken)
        ).subscribe((user) => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe((_user) => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const threasholdsObs = self.appService.GetTestThresholds(self.User.AuthToken);

            combineLatest(threasholdsObs, function (threshes) {
                return {
                    Thresholds: threshes,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    (packet) => {
                        this.TestsList = packet.Thresholds;

                        if (!this.TestsList || !this.TestsList.length) {
                            self.NoData = true;

                            // Put in a fake record to trip the display message
                            self.TestsData.dataChange.next([{} as any]);
                        } else {
                            self.NoData = false;
                            self.UpdateFilter();
                            // self.TestsData.dataChange.next(self.TestsList);
                        }

                        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                        // Ready for component use.
                        self.PartialViewReady = true;
                    },
                    (error) => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Activate Test

    ActivateTest(test: TestThresholdInfo) {
        const self = this;

        test.Active = true;

        // User has opted to proceed. Signal the system to remove this term window from the college.
        self.appService.UpdateTestThreshold(self.User.AuthToken, self.User.CurrentRole, test, this.User).subscribe(
            (test) => {
                // Toast the success
                self.splashService.SnackbarSuccess('Test score threshold activated successfully.');

                // Find the previous entry in the loaded list, update it by removing the score and clearing the active flag, then
                // reset the data source.
                const entry = self.TestsList.find((t) => t.TestThresholdID === test.TestThresholdID);
                if (entry != null) {
                    entry.Active = true;
                }

                self.TestsData.Update(self.TestsList);
            },
            (error) => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    // #endregion

    // #region Deactivate Test

    DeactivateTest(test: TestThresholdInfo) {
        const self = this;

        test.Active = false;

        // User has opted to proceed. Signal the system to remove this term window from the college.
        self.appService.UpdateTestThreshold(self.User.AuthToken, self.User.CurrentRole, test, this.User).subscribe(
            (test) => {
                // Toast the success
                self.splashService.SnackbarSuccess('Test score threshold activated successfully.');

                // Find the previous entry in the loaded list, update it by removing the score and clearing the active flag, then
                // reset the data source.
                const entry = self.TestsList.find((t) => t.TestThresholdID === test.TestThresholdID);
                if (entry != null) {
                    entry.Active = false;
                }

                self.TestsData.Update(self.TestsList);
            },
            (error) => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    // #endregion

    // #region Edit Test

    EditTest(test: TestThresholdInfo) {
        const self = this;

        self.dialogService
            .OpenEditTestThresholdDialog({
                TitleText: 'Edit Test Score',
                MessageText: `You have chosen to edit the following test score cutoff:<br /><br />
                                <strong>Test: </strong>${test.TestDescription}<br />
                                <strong>Subject: </strong>${test.Description}<br />` +
                                (test.Score ? `<strong>Score: </strong>${test.Score}<br />` : ''),
                OKText: 'Yes - SAVE CHANGES',
                CancelText: 'No - CANCEL',
                TestThresholdInfo: test,
            })
            .subscribe((result) => {
                if (result) {
                    test.Score = result;

                    // User has opted to proceed. Signal the system to remove this term window from the college.
                    self.appService.UpdateTestThreshold(self.User.AuthToken, self.User.CurrentRole, test, this.User).subscribe(
                        (test) => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('Test score threshold updated successfully.');

                            // Find the previous entry in the loaded list, update it by removing the score and clearing the active flag, then
                            // reset the data source.
                            const entry = self.TestsList.find((t) => t.TestThresholdID === test.TestThresholdID);
                            if (entry != null) {
                                entry.TestThresholdID = test.TestThresholdID;
                                entry.TestDescription = test.TestDescription;
                                entry.ContentCode = test.ContentCode;
                                entry.Description = test.Description;
                                entry.TestName = test.TestName;
                                entry.ScoreType = test.ScoreType;
                                entry.Score = test.Score;
                                entry.Active = test.Active;
                                entry.DateUpdated = test.DateUpdated;
                                entry.UpdatedBy = test.UpdatedBy;
                                entry.UpdatedByLastName = test.UpdatedByLastName;
                                entry.UpdatedByFirstName = test.UpdatedByFirstName;
                                entry.UpdatedByLocalJobTitle = test.UpdatedByLocalJobTitle;
                            }

                            self.TestsData.Update(self.TestsList);
                        },
                        (error) => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Remove Test

    RemoveTest(test: TestThresholdInfo) {
        const self = this;

        if (!test) {
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You have chosen to remove the following test score cutoff:<br /><br />
            <strong>Test: </strong>${test.TestDescription}<br />
            <strong>Subject: </strong>${test.Description}<br />
            <strong>Score: </strong>${test.Score}<br />
            <br /><br />Once removed, this test score cutoff must be added again in order for it to appear in the DELTA system. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove Test Cutoff',
                CancelText: 'No - CANCEL',
            })
            .subscribe((result) => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to remove this term window from the college.
                    self.appService.RemoveTestThreshold(self.User.AuthToken, self.User.CurrentRole, test.TestThresholdID).subscribe(
                        (testID) => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('Test score threshold removed successfully.');

                            // Find the previous entry in the loaded list, update it by removing the score and clearing the active flag, then
                            // reset the data source.
                            const entry = self.TestsList.find((t) => t.TestThresholdID === testID);
                            if (entry != null) {
                                entry.Active = false;
                                entry.Score = null;
                            }

                            self.TestsData.Update(self.TestsList);
                        },
                        (error) => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Sort Tests Method

    SortTests(First: TestThresholdInfo, Second: TestThresholdInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'TestDescription':
                [PropA, PropB] = [First.TestDescription, Second.TestDescription];
                break;
            case 'Description':
                [PropA, PropB] = [First.Description, Second.Description];
                break;
            case 'Active':
                [PropA, PropB] = [First.Active === true ? 'Yes' : 'No', Second.Active === true ? 'Yes' : 'No'];
                break;
        }

        if (PropA instanceof Date) {
            return (isBefore(PropA, PropB) ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Update Filter

    UpdateFilter() {
        const self = this;

        const ltext = self.FilterText.toLocaleLowerCase();

        self.FilteredTests = (self.TestsList || []).filter((t) => {
            return (
                (!self.OnlyActive || t.Active === true) &&
                (!ltext ||
                    t.TestDescription.toLocaleLowerCase().indexOf(ltext) >= 0 ||
                    t.Description.toLocaleLowerCase().indexOf(ltext) >= 0)
            );
        });

        self.TestsData.dataChange.next(self.FilteredTests);
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/managescores/managescores.component.ts